import { TipoPeriodo } from '../../models/dashboard/periodo-filtro.model';
import {
  comHoraFim,
  comHoraInicio,
  desdeAncora,
  hoje,
  intervaloDeMes,
  mesAnterior,
  mesAtual,
  ontem,
  ontemEHoje,
  proximoMes,
  ultimos30,
} from './periodo-core.util';

/**
 * Lista COMPLETA de presets de periodo (10 opcoes), na ordem usada no mobile.
 * "Mostrar todos" (showAll) e UNIVERSAL: aparece em todas as telas, alinhado
 * ao app mobile.
 * Usada por: dashboard-header, contas-a-pagar, contas-a-receber,
 * contas-pagar-receber e movimentacao (e e o default de `opcoes` do
 * app-periodo-filtro).
 */
export const PRESETS_PERIODO_PADRAO: TipoPeriodo[] = [
  'today', 'untilToday', 'yesterdayAndToday', 'thisWeek', 'thisMonth',
  'untilMonthEnd', 'prevMonth', 'nextMonth', 'last30', 'showAll',
];

/**
 * Lista de presets dos relatorios (a-pagar, a-receber, pagas, recebidas,
 * itens-vendidos, vendas, dre). Como "Mostrar todos" virou universal, e
 * identica a PRESETS_PERIODO_PADRAO — mantida como alias para nao alterar
 * os imports dos relatorios.
 */
export const PRESETS_PERIODO_RELATORIO: TipoPeriodo[] = PRESETS_PERIODO_PADRAO;

/**
 * Lista de presets SEM "Ontem e hoje" (9 opcoes), com "Mostrar todos" ao final.
 * Usada por: pedidos, nova-despesa, nova-receita e nova-transferencia.
 */
export const PRESETS_PERIODO_SEM_ONTEM_E_HOJE: TipoPeriodo[] = [
  'today', 'untilToday', 'thisWeek', 'thisMonth',
  'untilMonthEnd', 'prevMonth', 'nextMonth', 'last30', 'showAll',
];

/**
 * Lista de presets do dashboard de contas (8 opcoes, sem "Hoje" e sem
 * "Ontem e hoje"), com "Mostrar todos" ao final. Usada por: dashboard-contas.
 */
export const PRESETS_PERIODO_DASHBOARD_CONTAS: TipoPeriodo[] = [
  'untilToday', 'thisWeek', 'thisMonth',
  'untilMonthEnd', 'prevMonth', 'nextMonth', 'last30', 'showAll',
];

/**
 * Preenche com zero a esquerda numeros de 1 digito.
 */
export function pad(n: number): string {
  return n < 10 ? ('0' + n) : String(n);
}

/**
 * Formata Date para string no formato DD/MM/AAAAT HH:MM
 * (formato usado pela API do backend para filtros de periodo).
 */
export function fmtDateTime(d: Date): string {
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Inverso de `fmtDateTime`: converte `dd/MM/yyyyTHH:mm` em `Date` LOCAL.
 * Retorna `null` para qualquer valor fora do formato ou com data/hora impossivel
 * (ex.: 31/02) — nunca "corrige" silenciosamente para outro dia.
 */
export function parseDateTime(valor: string | null | undefined): Date | null {
  if (!valor) return null;
  const m = /^(\d{2})\/(\d{2})\/(\d{4})T(\d{2}):(\d{2})$/.exec(String(valor).trim());
  if (!m) return null;
  const [dia, mes, ano, hora, minuto] = m.slice(1).map(Number);
  if (!dataPlausivel(ano, mes, dia) || hora > 23 || minuto > 59) return null;
  return new Date(ano, mes - 1, dia, hora, minuto, 0, 0);
}

/**
 * Formata um Date como ISO `yyyy-MM-dd` usando os campos LOCAIS da data.
 *
 * NAO usa `toISOString()`: em UTC-3/UTC-4 a data em UTC nunca RECUA — nas horas finais do dia
 * ela AVANCA (01/09 23:59 vira 02/09). Por isso um teste escrito sobre a meia-noite nao protege
 * nada: a meia-noite empata nos dois sentidos, e o defeito so aparece no fim do dia. Endpoints
 * que recebem `LocalDate` do backend precisam do dia que o usuario escolheu, e nao do dia em
 * Greenwich.
 */
export function fmtDataIso(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Valida um trio ano/mes/dia sem construir `Date` (construir aceitaria 31/02 e "corrigiria"
 * para 03/03, mandando ao backend um dia que o usuario nunca escolheu).
 */
function dataPlausivel(ano: number, mes: number, dia: number): boolean {
  if (!Number.isFinite(ano) || !Number.isFinite(mes) || !Number.isFinite(dia)) return false;
  if (ano < 1000 || ano > 9999) return false;
  if (mes < 1 || mes > 12) return false;
  const diasNoMes = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const bissexto = (ano % 4 === 0 && ano % 100 !== 0) || ano % 400 === 0;
  const maximo = mes === 2 && bissexto ? 29 : diasNoMes[mes - 1];
  return dia >= 1 && dia <= maximo;
}

/**
 * Converte o valor emitido por `app-periodo-filtro` (`PeriodoFiltro.dataInicio` / `.dataFim`,
 * produzido por `fmtDateTime`) para ISO `yyyy-MM-dd`.
 *
 * Aceita:
 *  - `dd/MM/yyyyTHH:mm`  -> formato REAL emitido hoje
 *  - `dd/MM/yyyy`        -> defensivo, mesma origem sem a parte de hora
 *  - `yyyy-MM-dd...`     -> defensivo: se um dia `fmtDateTime` passar a emitir ISO (como o
 *                           FORM_UI ja afirma), esta funcao continua correta sem alteracao
 *
 * Qualquer outra coisa devolve `null` — o chamador OMITE o parametro em vez de mandar lixo e
 * receber 500. Um `slice(0, 10)` no valor cru mandaria `01/09/2026`, que o Spring nao converte.
 *
 * Nao constroi `Date` em nenhum caminho: e rearranjo de string, imune a fuso.
 */
export function paraDataIso(valor: string | null | undefined): string | null {
  if (valor == null) return null;
  const bruto = String(valor).trim();
  if (bruto.length === 0) return null;

  // Descarta a parte de hora, seja ela separada por 'T' (fmtDateTime) ou por espaco.
  const soData = bruto.split('T')[0].split(' ')[0];

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(soData);
  if (iso) {
    const ano = Number(iso[1]);
    const mes = Number(iso[2]);
    const dia = Number(iso[3]);
    return dataPlausivel(ano, mes, dia) ? `${ano}-${pad(mes)}-${pad(dia)}` : null;
  }

  const brasileiro = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(soData);
  if (brasileiro) {
    const dia = Number(brasileiro[1]);
    const mes = Number(brasileiro[2]);
    const ano = Number(brasileiro[3]);
    return dataPlausivel(ano, mes, dia) ? `${ano}-${pad(mes)}-${pad(dia)}` : null;
  }

  return null;
}

/**
 * Retorna primeiro e ultimo dia de um mes/ano como objetos Date.
 * start: dia 1 as 00:00
 * end: ultimo dia as 23:59
 *
 * Wrapper sobre `intervaloDeMes` do nucleo, aplicando o boundary da TELA
 * (00:00 / 23:59 sem segundos) para preservar o comportamento historico.
 */
export function firstAndLastOfMonth(month: number, year: number): { start: Date; end: Date } {
  const { start, end } = intervaloDeMes(month, year);
  return {
    start: comHoraInicio(start, 0, 0),
    end: comHoraFim(end, 23, 59),
  };
}

/**
 * Calcula as datas de inicio e fim para um TipoPeriodo predefinido.
 * Recebe labels traduzidos como parametro para manter a funcao pura (sem dependencia de translate).
 * Aceita horarios customizaveis para suportar modo avancado (time pickers).
 *
 * Delega o calculo dos DIAS-ANCORA ao nucleo (`periodo-core.util.ts`) e
 * aplica o boundary da TELA: start em `startHour:startMinute` (default 00:00)
 * e end em `endHour:endMinute` (default 23:59, sem segundos).
 */
export function calcularPeriodoPredefinido(
  tipo: TipoPeriodo,
  today: Date,
  labels: Record<TipoPeriodo, string>,
  startHour: number = 0,
  startMinute: number = 0,
  endHour: number = 23,
  endMinute: number = 59
): { start: Date; end: Date; texto: string } {
  // Aplica o boundary da TELA a um par de dias-ancora vindo do nucleo.
  const aplicarBoundary = (
    ancora: { start: Date; end: Date },
    texto: string,
  ): { start: Date; end: Date; texto: string } => ({
    start: comHoraInicio(ancora.start, startHour, startMinute),
    end: comHoraFim(ancora.end, endHour, endMinute),
    texto,
  });

  switch (tipo) {
    case 'today':
      return aplicarBoundary(hoje(today), labels['today']);
    case 'yesterday':
      return aplicarBoundary(ontem(today), labels['yesterday']);
    case 'untilToday':
      // Tudo ate hoje: 01/01/2000 (via desdeAncora) -> instante atual; alinhado
      // aos relatorios. Ignora os time pickers (ocultos neste preset).
      return {
        start: comHoraInicio(desdeAncora(today).start, 0, 0),
        end: new Date(today),
        texto: labels['untilToday'],
      };
    case 'yesterdayAndToday':
      return aplicarBoundary(ontemEHoje(today), labels['yesterdayAndToday']);
    case 'thisWeek': {
      // Semana DOMINGO -> SABADO (padronizada com os relatorios em 2026-06-12;
      // a primitiva `semanaCivil` do nucleo e segunda -> domingo e deixou de
      // ser usada aqui).
      const domingo = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - today.getDay(), // getDay(): 0=Domingo
      );
      const sabado = new Date(domingo);
      sabado.setDate(domingo.getDate() + 6);
      return aplicarBoundary({ start: domingo, end: sabado }, labels['thisWeek']);
    }
    case 'thisMonth':
      return aplicarBoundary(mesAtual(today), labels['thisMonth']);
    case 'untilMonthEnd':
      // Instante atual -> ultimo dia do mes 23:59 (padronizado com os
      // relatorios em 2026-06-12; antes ancorava em 01/01/2000). Ignora os
      // time pickers — o componente os oculta neste preset.
      return {
        start: new Date(today),
        end: comHoraFim(mesAtual(today).end, 23, 59),
        texto: labels['untilMonthEnd'],
      };
    case 'prevMonth':
      return aplicarBoundary(mesAnterior(today), labels['prevMonth']);
    case 'nextMonth':
      return aplicarBoundary(proximoMes(today), labels['nextMonth']);
    case 'last30':
      return aplicarBoundary(ultimos30(today), labels['last30']);
    case 'showAll':
      // "Mostrar todos": intervalo fixo 01/01/2000 -> 31/12/3000 (equivale ao
      // MOSTRAR_TODOS antigo dos relatorios). Datas fixas, sem time pickers.
      return aplicarBoundary(
        { start: new Date(2000, 0, 1), end: new Date(3000, 11, 31) },
        labels['showAll'],
      );
    default: {
      // Mantem o fallback historico: dia atual 00:00 -> 23:59 (sem texto).
      const ancora = hoje(today);
      return {
        start: new Date(ancora.start.getFullYear(), ancora.start.getMonth(), ancora.start.getDate(), 0, 0),
        end: new Date(ancora.end.getFullYear(), ancora.end.getMonth(), ancora.end.getDate(), 23, 59),
        texto: '',
      };
    }
  }
}
